import type { SceneComposition, SceneObject } from '../types/engine.types';

/**
 * Interface for scene builders that convert domain data to scene compositions.
 *
 * Scene builders are adapters that transform domain-specific data (like kitchen
 * blueprints, structure configurations, appliance specs) into generic scene
 * compositions that the ThreeSceneEngine can render.
 *
 * @example
 * ```typescript
 * class KitchenSceneBuilder implements ISceneBuilder<KitchenBlueprint> {
 *   readonly name = 'kitchen';
 *
 *   async build(blueprint: KitchenBlueprint): Promise<SceneComposition> {
 *     // Convert blueprint to scene objects
 *     return {
 *       objects: [{ object: islandMesh, metadata: { id: 'island-1', type: 'island' } }],
 *       bounds: { min: { x: -50, y: 0, z: -30 }, max: { x: 50, y: 40, z: 30 } }
 *     };
 *   }
 *
 *   dispose(): void {
 *     // Clean up resources
 *   }
 * }
 * ```
 */
export interface ISceneBuilder<TInput = unknown> {
	/**
	 * Unique name identifying this builder.
	 * Used when registering with the engine and in data sources.
	 */
	readonly name: string;

	/**
	 * Build a scene composition from input data.
	 *
	 * @param input - Domain-specific input data
	 * @returns Promise or direct SceneComposition
	 */
	build(input: TInput): Promise<SceneComposition> | SceneComposition;

	/**
	 * Progressive build - yields objects as they're created for immediate display.
	 * Objects are added to the scene as they're yielded, enabling progressive rendering.
	 * The generator returns the final SceneComposition with bounds and camera config.
	 *
	 * @param input - Domain-specific input data
	 * @returns AsyncGenerator that yields SceneObjects and returns final composition
	 */
	buildProgressive?(input: TInput): AsyncGenerator<SceneObject, SceneComposition, void>;

	/**
	 * Optional cleanup method.
	 * Called when the builder is unregistered or the engine is disposed.
	 */
	dispose?(): void;
}

/**
 * Base class for scene builders with common utility methods.
 */
export abstract class BaseSceneBuilder<TInput = unknown> implements ISceneBuilder<TInput> {
	abstract readonly name: string;

	abstract build(input: TInput): Promise<SceneComposition> | SceneComposition;

	dispose(): void {
		// Override in subclasses if cleanup is needed
	}

	/**
	 * Create an empty scene composition
	 */
	protected createEmptyComposition(): SceneComposition {
		return {
			objects: [],
			bounds: undefined,
			camera: undefined,
			lighting: undefined
		};
	}

	/**
	 * Calculate bounds from a list of positions
	 */
	protected calculateBounds(
		positions: Array<{ x: number; y: number; z: number }>
	): { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } } {
		if (positions.length === 0) {
			return {
				min: { x: 0, y: 0, z: 0 },
				max: { x: 0, y: 0, z: 0 }
			};
		}

		const min = { x: Infinity, y: Infinity, z: Infinity };
		const max = { x: -Infinity, y: -Infinity, z: -Infinity };

		for (const pos of positions) {
			min.x = Math.min(min.x, pos.x);
			min.y = Math.min(min.y, pos.y);
			min.z = Math.min(min.z, pos.z);
			max.x = Math.max(max.x, pos.x);
			max.y = Math.max(max.y, pos.y);
			max.z = Math.max(max.z, pos.z);
		}

		return { min, max };
	}
}


